import {
	CloseOutlined,
	InfoCircleOutlined,
	LoadingOutlined,
	MinusCircleOutlined,
	PlusCircleOutlined,
} from '@ant-design/icons';
import { Col, Divider, Modal, Row } from 'antd';
import Title from 'antd/es/typography/Title';
import Buttons from 'components/Global/Buttons';
import InputNumberField from 'components/Global/InputNumberField';
import { useEffect, useState } from 'react';
import styles from './index.module.scss';
import { toastText } from 'utils/utils';
import { leaveManagementApi } from 'Api/masters/leave-management';

type ConfigRuleModalProps = {
	open: boolean;
	leaveManagementId: any;
	fetchLeaveManagementData: any;
	leaveName: string | null;
	configLoading: boolean;
	closeModal: any;
	setConfigLoading: any;
	EditForConfig: boolean;
	leaveHistoryId: string;
};

const ConfigRuleModal = (props: ConfigRuleModalProps) => {
	const {
		open,
		leaveManagementId,
		fetchLeaveManagementData,
		leaveName,
		closeModal,
		configLoading,
		setConfigLoading,
		EditForConfig,
		leaveHistoryId,
	} = props;

	const [loading, setLoading] = useState(false);
	const [rows, setRows] = useState<any>([]);
	const [errors, setErrors] = useState<any>({});
	const [showInfo, setShowInfo] = useState(false);

	// Information modal
	const getInfoModal = () => (
		<Modal
			open={showInfo}
			onCancel={() => setShowInfo(false)}
			footer={null}
			width={720}
			closable={false}
		>
			<div className={styles['modal-header']}>
				<Title level={4}>Leave Configuration Rules</Title>
				<div
					className={styles['close-icon']}
					onClick={() => setShowInfo(false)}
				>
					<CloseOutlined />
				</div>
			</div>
			<div className={styles.infoModalBody}>
				<p>
					<strong>
						Configure leave entitlements based on employee
						experience:
					</strong>
				</p>
				<ul className={styles.infoModalList}>
					<li>
						<strong>Minimum Experience:</strong> Minimum completed
						months of experience required for this leave tier
					</li>
					<li>
						<strong>Maximum Experience:</strong> Maximum completed
						months of experience covered in this leave tier
					</li>
					<li>
						<strong>Number of Leaves:</strong> Total leave days
						entitled for employees in this experience range
					</li>
				</ul>
				<p>
					<strong>Validation Rules:</strong>
				</p>
				<ul className={styles.infoModalList}>
					<li>Experience ranges must not overlap</li>
					<li>
						Each tier must have more leaves than the previous tier
					</li>
					<li>Minimum and maximum months must be whole numbers</li>
					<li>
						The final tier covers remaining experience with no max
						limit
					</li>
				</ul>
			</div>
		</Modal>
	);

	// Helper function to format experience range for display
	const formatExperienceRange = (
		minMonth: number,
		maxMonth: number | null
	) => {
		if (maxMonth === null || maxMonth === Infinity) {
			return `${minMonth}+ months of experience`;
		}
		return `${minMonth}-${maxMonth} months of experience`;
	};
	// Comprehensive validation function that checks all rows
	const validateAllRows = (updatedRows: any[]) => {
		const newErrors: any = {};

		updatedRows.forEach((row: any, index: number) => {
			const rowErrors: any = {};

			// Validate minMonth
			if (row.minMonth === null || row.minMonth === undefined) {
				rowErrors.minMonth = 'Min month is required';
			} else if (!Number.isInteger(row.minMonth)) {
				rowErrors.minMonth = 'Min month must be a whole number';
			} else if (row.minMonth < 0) {
				rowErrors.minMonth = 'Min month cannot be negative';
			}
			// else if (index === 0 && row.minMonth !== 0) {
			// 	rowErrors.minMonth = 'First row min month should be 0';
			// }
			else if (index > 0) {
				const prevRow = updatedRows[index - 1];
				if (
					prevRow &&
					prevRow.maxMonth !== null &&
					prevRow.maxMonth !== Infinity
				) {
					if (row.minMonth <= prevRow.maxMonth) {
						rowErrors.minMonth = `Min month should be greater than ${prevRow.maxMonth}`;
					}
				}
			}

			// Validate maxMonth
			if (row.maxMonth !== Infinity && row.maxMonth !== null) {
				if (!Number.isInteger(row.maxMonth)) {
					rowErrors.maxMonth = 'Max month must be a whole number';
				} else if (row.maxMonth < 0) {
					rowErrors.maxMonth = 'Max month cannot be negative';
				} else if (
					row.minMonth !== null &&
					row.maxMonth <= row.minMonth
				) {
					rowErrors.maxMonth = `Max month must be greater than ${row.minMonth}`;
				}
			}

			// For non-last rows, maxMonth shouldn't be null or Infinity
			if (
				index < updatedRows.length - 1 &&
				(row.maxMonth === null || row.maxMonth === Infinity)
			) {
				rowErrors.maxMonth = 'Max month is required';
			}

			// Validate leaves
			if (row.leaves === null || row.leaves === undefined) {
				rowErrors.leaves = 'Number of leaves is required';
			} else if (!Number.isInteger(row.leaves)) {
				rowErrors.leaves = 'Number of leaves must be a whole number';
			} else if (row.leaves < 0) {
				rowErrors.leaves = 'Number of leaves cannot be negative';
			} else if (index > 0 && row.leaves <= 0) {
				rowErrors.leaves = 'Leaves must be greater than 0';
			}

			// Check if leaves are increasing (business rule)
			if (
				index > 0 &&
				row.leaves !== null &&
				updatedRows[index - 1].leaves !== null
			) {
				if (row.leaves <= updatedRows[index - 1].leaves) {
					rowErrors.leaves = 'Leaves should increase with tenure';
				}
			}

			if (Object.keys(rowErrors).length > 0) {
				newErrors[index] = rowErrors;
			}
		});

		return newErrors;
	};

	const handleInputChange = (
		index: number,
		field: string,
		value: number | null
	) => {
		const newRows = [...rows];
		newRows[index][field] = value;
		setRows(newRows);

		// Only validate the last row
		const newErrors = validateAllRows(newRows);
		setErrors(newErrors);
		if (index !== newRows.length - 1) {
			const updatedErrors = { ...newErrors };
			delete updatedErrors[newRows.length - 1];
			setErrors(updatedErrors);
		}
	};

	const isRowFilled = () => {
		return rows.every((row: any, index: number) => {
			const hasRequiredFields =
				row.minMonth !== null &&
				row.leaves !== null &&
				(index === rows.length - 1 || row.maxMonth !== null);

			const hasNoErrors =
				!errors[index] || Object.keys(errors[index]).length === 0;

			return hasRequiredFields && hasNoErrors;
		});
	};

	const addRow = (index: number) => {
		const newRows = [...rows];
		const lastRow = newRows.at(-1);

		// Set the new row's minMonth based on the previous row's maxMonth
		const newMinMonth =
			lastRow.maxMonth !== null && lastRow.maxMonth !== Infinity
				? lastRow.maxMonth + 1
				: null;

		newRows.push({
			minMonth: newMinMonth,
			maxMonth: Infinity,
			leaves: null,
		});

		// Update the previous last row to have a specific maxMonth instead of Infinity
		if (newRows.length > 1) {
			const prevLastRow = newRows.at(-2);
			if (prevLastRow.maxMonth === Infinity) {
				prevLastRow.maxMonth = null;
			}
		}

		setRows(newRows);

		// Validate all rows after adding
		// const newErrors = validateAllRows(newRows); // skip empty row validation
		// setErrors(newErrors);
	};

	const removeRow = (index: number) => {
		const newRows = rows.filter((_: any, i: number) => i !== index);

		// Ensure the last row has Infinity as maxMonth
		if (newRows.length > 0) {
			newRows[newRows.length - 1].maxMonth = Infinity;
		}

		setRows(newRows);

		// Remove errors for the deleted row and revalidate
		const newErrors = validateAllRows(newRows);
		setErrors(newErrors);
	};

	const closeConfigurationModal = () => {
		setRows([]);
		setErrors({});
		closeModal();
		fetchLeaveManagementData();
	};

	const handleSave = async () => {
		// Final validation before save
		const finalErrors = validateAllRows(rows);
		setErrors(finalErrors);

		if (Object.keys(finalErrors).length > 0) {
			return;
		}

		try {
			const _configurationRules = rows.map((row: any) => ({
				minMonth: row.minMonth,
				maxMonth: row.maxMonth === Infinity ? -1 : row.maxMonth,
				leaves: row.leaves,
			}));

			if (EditForConfig) {
				try {
					setConfigLoading(true);
					await leaveManagementApi.editLeaveConfiguration(
						leaveManagementId,
						{
							leaveHistoryId: leaveHistoryId,
							configurationRules: _configurationRules,
						}
					);
					toastText('Configuration updated successfully', 'success');
					closeConfigurationModal();
				} catch (error: any) {
					let message =
						error?.response?.data?.message ||
						'Something went wrong in editing configuration';
					toastText(message, 'error');
				} finally {
					setConfigLoading(false);
				}
			} else {
				try {
					setConfigLoading(true);
					await leaveManagementApi.addConfigurationRoles(
						leaveManagementId,
						{
							configurationRules: _configurationRules,
						}
					);
					toastText('Configuration saved successfully', 'success');
					closeConfigurationModal();
				} catch (error: any) {
					let message =
						error?.response?.data?.message ||
						'Something went wrong in saving configuration';
					toastText(message, 'error');
				} finally {
					setConfigLoading(false);
				}
			}
		} catch (error) {
			let message =
				'Something went wrong in editing configuration';
			toastText(message, 'error');
		}
	};

	const buttons = [
		{
			text: 'Cancel',
			isLoading: false,
			className: 'btn-cancel',
			isSubmit: true,
			disabled: configLoading,
			onclick: closeConfigurationModal,
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
		},
		{
			text: 'Save',
			isLoading: configLoading,
			className: 'btn-blue',
			isSubmit: true,
			disabled: false,
			onclick: handleSave,
			fontSize: '1.8rem',
			minWidth: '12rem',
			minHeight: '4rem',
		},
	];

	const fetchConfigurations = async () => {
		try {
			setLoading(true);
			const response = await leaveManagementApi.getLeaveConfigurations(
				leaveManagementId
			);
			if (response?.data?.data?.length === 0) {
				setRows([{ minMonth: 0, maxMonth: Infinity, leaves: 0 }]);
			} else {
				const configurationRules = response?.data?.data.sort(
					(a: any, b: any) => a.minMonth - b.minMonth
				);

				configurationRules.forEach((rule: any) => {
					if (rule.maxMonth === -1) {
						rule.maxMonth = Infinity;
					}
				});
				setRows(configurationRules);
			}
		} catch (error: any) {
			const message =
				error?.response?.data?.error?.description ||
				'Something went wrong in fetching configurations';
			toastText(message, 'error');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (open && leaveManagementId) {
			fetchConfigurations();
		}
	}, [open, leaveManagementId]);

	return (
		<>
			<Modal
				open={open}
				onCancel={closeConfigurationModal}
				footer={null}
				width={1000}
				maskClosable={false}
				closable={false}
			>
				<div className={`${styles['modal']} modal`}>
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							marginBottom: '24px',
						}}
					>
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '8px',
							}}
						>
							<Title level={4} style={{ margin: 0 }}>
								{leaveName} Configuration
							</Title>
							<InfoCircleOutlined
								style={{
									color: '#584495',
									cursor: 'pointer',
									fontSize: '16px',
								}}
								onClick={() => setShowInfo(true)}
							/>
						</div>
						<CloseOutlined
							style={{
								cursor: 'pointer',
								fontSize: '16px',
								padding: '4px',
							}}
							onClick={closeConfigurationModal}
						/>
					</div>
					<Divider />
					{loading ? (
						<div
							style={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
							}}
						>
							<LoadingOutlined
								style={{ fontSize: '100px', color: '#584495' }}
							/>
						</div>
					) : (
						<div className={styles['modal-body']}>
							<Row
								className={styles['form-container-card']}
								gutter={50}
							>
								<Row
									className={styles['row']}
									align="middle"
									gutter={15}
								>
									<Col span={7} className={styles['col']}>
										<div
											className={
												styles['input-container']
											}
										>
											<p
												className={
													styles['input-title']
												}
											>
												Minimum Experience{' '}
												<span
													className={styles['text']}
												>
													(in months)
												</span>
											</p>
										</div>
									</Col>
									<Col span={8} className={styles['col']}>
										<div
											className={
												styles['input-container']
											}
										>
											<p
												className={
													styles['input-title']
												}
											>
												Maximum Experience{' '}
												<span
													className={styles['text']}
												>
													(in months)
												</span>
											</p>
										</div>
									</Col>
									<Col span={6} className={styles['col']}>
										<div
											className={
												styles['input-container']
											}
										>
											<p
												className={
													styles['input-title']
												}
											>
												Number of Leaves
											</p>
										</div>
									</Col>
								</Row>
								{rows?.map((row: any, index: number) => (
									<>
										<Row
											key={index}
											className={styles['row']}
											align="middle"
											gutter={15}
										>
											<Col
												span={7}
												className={styles['col']}
											>
												<div
													className={
														styles[
															'input-container'
														]
													}
												>
													<InputNumberField
														required
														name="minMonth"
														placeholder="Min Month"
														style={{
															width: '100%',
														}}
														helperText=""
														value={row.minMonth}
														size="large"
														min={0}
														onChange={(
															value: any
														) =>
															handleInputChange(
																index,
																'minMonth',
																value
															)
														}
														isError={
															!!errors[index]
																?.minMonth
														}
													/>
													<div
														className={
															styles[
																'error-message'
															]
														}
													>
														{errors[index]
															?.minMonth ||
															'\u00A0'}
													</div>
												</div>
											</Col>
											<Col
												span={7}
												className={styles['col']}
											>
												<div
													className={
														styles[
															'input-container'
														]
													}
												>
													<InputNumberField
														required
														name="maxMonth"
														placeholder="Max"
														helperText=""
														value={
															row.maxMonth ===
															Infinity
																? undefined
																: row.maxMonth
														}
														size="large"
														style={{
															width: '100%',
														}}
														disabled={
															row.maxMonth ===
															Infinity
														}
														min={0}
														onChange={(
															value: any
														) =>
															handleInputChange(
																index,
																'maxMonth',
																value
															)
														}
														isError={
															!!errors[index]
																?.maxMonth
														}
													/>
													<div
														className={
															styles[
																'error-message'
															]
														}
													>
														{errors[index]
															?.maxMonth ||
															'\u00A0'}
													</div>
												</div>
											</Col>
											<Col
												span={1}
												className={styles['col']}
											>
												<div
													className={
														styles[
															'input-container'
														]
													}
												>
													<p
														style={{
															fontSize: '27px',
															marginTop: '-6px',
														}}
													>
														=
													</p>
												</div>
											</Col>
											<Col
												span={7}
												className={styles['col']}
											>
												<div
													className={
														styles[
															'input-container'
														]
													}
												>
													<InputNumberField
														placeholder="Number of Leaves"
														style={{
															width: '100%',
														}}
														helperText=""
														size="large"
														required
														name="leaves"
														value={row.leaves}
														min={0}
														onChange={(
															value: any
														) =>
															handleInputChange(
																index,
																'leaves',
																value
															)
														}
														isError={
															!!errors[index]
																?.leaves
														}
													/>
													<div
														className={
															styles[
																'error-message'
															]
														}
													>
														{errors[index]
															?.leaves ||
															'\u00A0'}
													</div>
												</div>
											</Col>
											<Col
												span={2}
												className={styles['col']}
												style={{
													textAlign: 'center',
												}}
											>
												<div
													className={
														styles['icon-container']
													}
												>
													{index === 0 ? (
														<PlusCircleOutlined
															onClick={
																!isRowFilled()
																	? undefined
																	: () =>
																			addRow(
																				rows.length
																			)
															}
															className={
																styles['icon']
															}
															style={{
																color: !isRowFilled()
																	? 'grey'
																	: 'black',
																cursor: !isRowFilled()
																	? 'not-allowed'
																	: 'pointer',
															}}
														/>
													) : (
														<MinusCircleOutlined
															onClick={() =>
																removeRow(index)
															}
															className={
																styles['icon']
															}
														/>
													)}
												</div>
											</Col>
										</Row>
										{/* Experience Range Display */}
										{row.minMonth !== null &&
											row.leaves !== null && (
												<div
													style={{
														padding: '8px 12px',
														width: '100%',

														textAlign: 'center',
														borderRadius: '4px',
														fontSize: '13px',
														color: '#584495',
														fontStyle: 'italic',
													}}
												>
													Employees with{' '}
													{formatExperienceRange(
														row.minMonth,
														row.maxMonth
													)}{' '}
													gets {row.leaves} leave days
													per financial year
												</div>
											)}
									</>
								))}
							</Row>
						</div>
					)}
					<Row className={styles['modal-buttons']}>
						<Buttons buttons={buttons} />
					</Row>
				</div>
			</Modal>
			{showInfo && getInfoModal()}
		</>
	);
};

export default ConfigRuleModal;
